import languageLocale from 'utils/LanguageLocale';

/**
 * Drops the trailing " to {ZIP}" / " au {postal code}" location segment from a
 * localized shipToHome message, keeping the backend-provided (already localized)
 * "Delivery by …" / "Livraison par …" prefix and date.
 *
 * The split token is locale-aware: `" to"` for English (US/CA-EN) and `" au"`
 * for CA-FR (no trailing space, matching the legacy GetItShipped split) so the
 * postal code is stripped in every locale.
 *
 * @param {string | undefined | null} message - The raw shipToHome message.
 * @returns {string} The message minus its trailing location segment, or an
 *     empty string if `message` is falsy.
 */
const stripTrailingLocation = message => {
    if (!message) {
        return '';
    }

    const separator = languageLocale.isFRCanada() ? ' au' : ' to';

    return String(message).split(separator)[0].trim();
};

/**
 * Formats the ETA shown on the Free Shipping card (SHP-5111).
 * Returns the shipToHome message minus its trailing location segment —
 * e.g. "Delivery by Thu, Nov 4" (US) or "Livraison par jeu. 4 nov." (CA-FR).
 *
 * @param {string | undefined | null} shipToHomeTitle - The raw shipToHome
 *     message from the Redux `shipToHomeSelector`.
 * @returns {string} The formatted card ETA, or an empty string if the input
 *     is falsy.
 */
export const formatCardEta = shipToHomeTitle => stripTrailingLocation(shipToHomeTitle);

/**
 * Formats the ETA shown in the shipping details panel (SHP-5111).
 * Returns the shipToHome message minus its trailing location segment so the
 * ZIP / postal code can be rendered separately as a clickable trigger for the
 * location modal. Example: "Delivery by Thu, Feb 14 to 94102" returns
 * "Delivery by Thu, Feb 14"; "Livraison par jeu. 4 nov. au H2X 1Y4" returns
 * "Livraison par jeu. 4 nov.".
 *
 * @param {string | undefined | null} shipToHomeMessage - The raw shipToHome
 *     message from the Redux `shipToHomeSelector`.
 * @returns {string} The formatted panel ETA, or an empty string if the input
 *     is falsy.
 */
export const formatPanelEta = shipToHomeMessage => stripTrailingLocation(shipToHomeMessage);
